// src/views/Staff/StaffStatistics.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import NumberInput from "../../components/Inputs/NumberInput";

import { getInstitutions } from "../../services/institutionService";
import { getSessions } from "../../services/sessionService";
import { getFocusAreas } from "../../services/focusAreaService";

import {
  getStaffStatistics,
  deleteStaffStatistics,
  batchUpdateStaffStatistics,
  createStaffStatistics,
} from "../../services/staffStatisticsService";

import { staffStatisticsSchema } from "../../validators/staffStatisticsValidators";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const { Option } = Select;

const StaffStatistics = () => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [errs, setErrs] = useState(null);

  const [institutions, setInstitutions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedSession, setSelectedSession] = useState("Not Selected");

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [expandedRowKey, setExpandedRowKey] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [instRes, sessRes, faRes, statRes] = await Promise.all([
        getInstitutions(),
        getSessions(),
        getFocusAreas(),
        getStaffStatistics(),
      ]);
      setInstitutions(instRes.data?.data || []);
      setSessions(sessRes.data?.data || []);
      setFocusAreas(faRes.data?.data || []);
      setStats(statRes.data?.data || []);
      setFilteredStats(statRes.data?.data || []);
    } catch (e) {
      toast.error("Failed to load staff statistics");
    } finally {
      setLoading(false);
    }
  };

  // Filters
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        (item.institution?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, stats, selectedInstitution, selectedSession]);

  // Group by Institution + Session (like Students/Graduation)
  const groupedStats = useMemo(() => {
    const grouped = new Map();
    for (const item of filteredStats) {
      const inst = item.institution;
      const sess = item.session;
      if (!inst || !sess) continue;

      const key = `${inst.id}__${sess.id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          institution: inst,
          session: sess,
          data: [],
          totals: {
            phd_full: 0,
            phd_part: 0,
            phd_retained: 0,
            prof_full: 0,
            prof_part: 0,
            prof_retained: 0,
            msc_full: 0,
            msc_part: 0,
            msc_retained: 0,
            total: 0,
          },
        });
      }
      const grp = grouped.get(key);
      grp.data.push(item);

      const phdF = item.phd_staff_count_full_time || 0;
      const phdP = item.phd_staff_count_part_time || 0;
      const phdR = item.phd_staff_count_retained || 0;

      const profF = item.professor_staff_count_full_time || 0;
      const profP = item.professor_staff_count_part_time || 0;
      const profR = item.professor_staff_count_retained || 0;

      const mscF = item.masters_staff_count_full_time || 0;
      const mscP = item.masters_staff_count_part_time || 0;
      const mscR = item.masters_staff_count_retained || 0;

      grp.totals.phd_full += phdF;
      grp.totals.phd_part += phdP;
      grp.totals.phd_retained += phdR;

      grp.totals.prof_full += profF;
      grp.totals.prof_part += profP;
      grp.totals.prof_retained += profR;

      grp.totals.msc_full += mscF;
      grp.totals.msc_part += mscP;
      grp.totals.msc_retained += mscR;

      grp.totals.total +=
        phdF + phdP + phdR + profF + profP + profR + mscF + mscP + mscR;
    }
    return Array.from(grouped.values());
  }, [filteredStats]);

  // Create (batch)
  const onCreate = async (payload, resetForm) => {
    setErrs(null);
    try {
      const { data } = await createStaffStatistics(payload);
      const newRows = data?.data || [];
      const next = [...stats, ...newRows];
      setStats(next);
      setFilteredStats(next);
      toast.success("Staff statistics added successfully");
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Failed to add staff statistics");
      }
    }
  };

  // Edit -> open modal with that group's rows
  const handleEdit = (group) => {
    setEditingGroup({
      institution: group.institution,
      session: group.session,
      data: group.data, // array with ids
    });
    setIsModalOpen(true);
  };

  // Batch update
  const onBatchUpdate = async (values, resetForm) => {
    setErrs(null);
    try {
      const { data } = await batchUpdateStaffStatistics(values);
      const updated = data?.data || [];

      const mapUpdated = new Map(updated.map((r) => [r.id, r]));
      const replaced = stats.map((row) =>
        mapUpdated.has(row.id) ? mapUpdated.get(row.id) : row
      );
      const newOnes = updated.filter((r) => !stats.find((x) => x.id === r.id));
      const finalState = [...replaced, ...newOnes];

      setStats(finalState);
      setFilteredStats(finalState);

      toast.success("Staff statistics updated successfully!");
      setIsModalOpen(false);
      setEditingGroup(null);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Failed to update staff statistics"
        );
      }
    }
  };

  // Delete single stat row
  const confirmDelete = (row) => {
    setRowToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const onDelete = async () => {
    const original = [...stats];
    const next = original.filter((s) => s.id !== rowToDelete.id);
    setStats(next);
    setFilteredStats(next);

    try {
      await deleteStaffStatistics(rowToDelete.id);
      toast.success("Deleted successfully!");
    } catch (e) {
      toast.error("Failed to delete. Try again later.");
      setStats(original);
      setFilteredStats(original);
    } finally {
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setEditingGroup(null);
    setRowToDelete(null);
    setErrs(null);
  };

  const columns = [
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: "institution",
            width: 320,
            render: (institution) => (
              <span className="font-semibold text-lg">{institution?.name}</span>
            ),
          },
        ]
      : []),
    {
      title: "Year",
      dataIndex: ["session", "name"],
      width: 140,
    },
    { title: "PhD FT", dataIndex: ["totals", "phd_full"], width: 100 },
    { title: "PhD PT", dataIndex: ["totals", "phd_part"], width: 100 },
    {
      title: "PhD Retained",
      dataIndex: ["totals", "phd_retained"],
      width: 120,
    },
    { title: "Professor FT", dataIndex: ["totals", "prof_full"], width: 120 },
    { title: "Professor PT", dataIndex: ["totals", "prof_part"], width: 120 },
    {
      title: "Professor Retained",
      dataIndex: ["totals", "prof_retained"],
      width: 150,
    },
    { title: "Masters FT", dataIndex: ["totals", "msc_full"], width: 110 },
    { title: "Masters PT", dataIndex: ["totals", "msc_part"], width: 110 },
    {
      title: "Masters Retained",
      dataIndex: ["totals", "msc_retained"],
      width: 150,
    },
    {
      title: "Total",
      dataIndex: ["totals", "total"],
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Staff Statistics">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Staff Statistics" subtitle1="Staff Statistics" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Staff Statistics"
            count={groupedStats.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Staff Statistics"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={groupedStats}
            expandable={{
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: (expanded, record) => {
                setExpandedRowKey(expanded ? record.key : null);
              },
              expandedRowRender: (record) => (
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Focus Area</th>
                      <th className="text-left p-2">PhD FT</th>
                      <th className="text-left p-2">PhD PT</th>
                      <th className="text-left p-2">PhD Retained</th>
                      <th className="text-left p-2">Professor FT</th>
                      <th className="text-left p-2">Professor PT</th>
                      <th className="text-left p-2">Professor Retained</th>
                      <th className="text-left p-2">Masters FT</th>
                      <th className="text-left p-2">Masters PT</th>
                      <th className="text-left p-2">Masters Retained</th>
                      <th className="text-left p-2">Total</th>
                      <th className="text-left p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-amber-50">
                    {record.data.map((item) => {
                      const rowTotal =
                        (item.phd_staff_count_full_time || 0) +
                        (item.phd_staff_count_part_time || 0) +
                        (item.phd_staff_count_retained || 0) +
                        (item.professor_staff_count_full_time || 0) +
                        (item.professor_staff_count_part_time || 0) +
                        (item.professor_staff_count_retained || 0) +
                        (item.masters_staff_count_full_time || 0) +
                        (item.masters_staff_count_part_time || 0) +
                        (item.masters_staff_count_retained || 0);

                      return (
                        <tr key={item.id}>
                          <td className="p-2 text-sm">
                            {item.focus_area?.name || "N/A"}
                          </td>
                          <td className="p-2 text-sm">
                            {item.phd_staff_count_full_time}
                          </td>
                          <td className="p-2 text-sm">
                            {item.phd_staff_count_part_time}
                          </td>
                          <td className="p-2 text-sm">
                            {item.phd_staff_count_retained}
                          </td>

                          <td className="p-2 text-sm">
                            {item.professor_staff_count_full_time}
                          </td>
                          <td className="p-2 text-sm">
                            {item.professor_staff_count_part_time}
                          </td>
                          <td className="p-2 text-sm">
                            {item.professor_staff_count_retained}
                          </td>

                          <td className="p-2 text-sm">
                            {item.masters_staff_count_full_time}
                          </td>
                          <td className="p-2 text-sm">
                            {item.masters_staff_count_part_time}
                          </td>
                          <td className="p-2 text-sm">
                            {item.masters_staff_count_retained}
                          </td>

                          <td className="p-2 text-sm">{rowTotal}</td>

                          <td className="p-1">
                            <Tooltip title="Delete">
                              <button
                                onClick={() => confirmDelete(item)}
                                className="text-red-500 p-1"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ),
            }}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => setSelectedInstitution(value)}
                    showSearch
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    <Option value={"Not Selected"}>
                      Filter by Institution
                    </Option>
                    {institutions.map((inst) => (
                      <Option key={inst.id} value={inst.id}>
                        {inst.name}
                      </Option>
                    ))}
                  </Select>
                )}

                <Select
                  multiple={false}
                  id="sessions"
                  defaultValue={selectedSession}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedSession(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value={"Not Selected"}>Filter by Year</Option>
                  {sessions.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {editingGroup
                    ? "Edit Staff Statistics"
                    : "Add Staff Statistics"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={1500}
          >
            <Formik
              initialValues={{
                institution_id: editingGroup
                  ? editingGroup.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                session_id: editingGroup ? editingGroup.session?.id : "",
                focus_area_stats: editingGroup?.data?.map((row) => ({
                  id: row.id,
                  focus_area_id: row.focus_area?.id || "",
                  phd_staff_count_full_time:
                    row.phd_staff_count_full_time ?? "",
                  phd_staff_count_part_time:
                    row.phd_staff_count_part_time ?? "",
                  phd_staff_count_retained: row.phd_staff_count_retained ?? "",

                  professor_staff_count_full_time:
                    row.professor_staff_count_full_time ?? "",
                  professor_staff_count_part_time:
                    row.professor_staff_count_part_time ?? "",
                  professor_staff_count_retained:
                    row.professor_staff_count_retained ?? "",

                  masters_staff_count_full_time:
                    row.masters_staff_count_full_time ?? "",
                  masters_staff_count_part_time:
                    row.masters_staff_count_part_time ?? "",
                  masters_staff_count_retained:
                    row.masters_staff_count_retained ?? "",
                })) || [
                  {
                    id: "",
                    focus_area_id: "",
                    phd_staff_count_full_time: "",
                    phd_staff_count_part_time: "",
                    phd_staff_count_retained: "",
                    professor_staff_count_full_time: "",
                    professor_staff_count_part_time: "",
                    professor_staff_count_retained: "",
                    masters_staff_count_full_time: "",
                    masters_staff_count_part_time: "",
                    masters_staff_count_retained: "",
                  },
                ],
              }}
              enableReinitialize
              validationSchema={staffStatisticsSchema}
              onSubmit={(values, { resetForm }) => {
                if (editingGroup) {
                  onBatchUpdate(values, resetForm);
                } else {
                  onCreate(values, resetForm);
                }
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => (
                <Form className="space-y-5 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-3 grid-cols-2">
                    {/* Institution */}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Institution <span className="text-red-500">*</span>
                      </label>
                      {isInstAdmin ? (
                        <input
                          type="hidden"
                          name="institution_id"
                          value={
                            editingGroup
                              ? editingGroup?.institution?.id
                              : user?.institution?.id
                          }
                        />
                      ) : (
                        <>
                          <Select
                            name="institution_id"
                            value={values.institution_id || undefined}
                            onChange={(val) => {
                              setFieldValue("institution_id", val);
                              // reset session + rows to avoid duplicates
                              setFieldValue("session_id", "");
                              setFieldValue("focus_area_stats", [
                                {
                                  focus_area_id: "",
                                  phd_staff_count_full_time: "",
                                  phd_staff_count_part_time: "",
                                  phd_staff_count_retained: "",
                                  professor_staff_count_full_time: "",
                                  professor_staff_count_part_time: "",
                                  professor_staff_count_retained: "",
                                  masters_staff_count_full_time: "",
                                  masters_staff_count_part_time: "",
                                  masters_staff_count_retained: "",
                                },
                              ]);
                            }}
                            placeholder="Select Institution"
                            className="w-full"
                            disabled={!!editingGroup}
                          >
                            {institutions.map((inst) => (
                              <Option key={inst.id} value={inst.id}>
                                {inst.name}
                              </Option>
                            ))}
                          </Select>
                          {errors.institution_id && touched.institution_id && (
                            <div className="text-red-500 text-xs">
                              {errors.institution_id}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Session */}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="session_id"
                        value={values.session_id || undefined}
                        onChange={(val) => {
                          setFieldValue("session_id", val);
                          // reset rows when session changes
                          setFieldValue("focus_area_stats", [
                            {
                              focus_area_id: "",
                              phd_staff_count_full_time: "",
                              phd_staff_count_part_time: "",
                              phd_staff_count_retained: "",
                              professor_staff_count_full_time: "",
                              professor_staff_count_part_time: "",
                              professor_staff_count_retained: "",
                              masters_staff_count_full_time: "",
                              masters_staff_count_part_time: "",
                              masters_staff_count_retained: "",
                            },
                          ]);
                        }}
                        placeholder="Select Year"
                        className="w-full"
                        disabled={!!editingGroup}
                      >
                        {sessions.map((s) => (
                          <Option key={s.id} value={s.id}>
                            {s.name}
                          </Option>
                        ))}
                      </Select>
                      {errors.session_id && touched.session_id && (
                        <div className="text-red-500 text-xs">
                          {errors.session_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rows */}
                  <FieldArray name="focus_area_stats">
                    {({ push, remove }) => (
                      <div className="space-y-4">
                        {values.focus_area_stats.map((_, index) => {
                          // enforce unique focus area per (institution, session)
                          const currentFAId =
                            values.focus_area_stats[index].focus_area_id;

                          const usedInForm = values.focus_area_stats
                            .map((r, i) => r.focus_area_id)
                            .filter((id, i) => i !== index);

                          const existingForCombo = stats
                            .filter(
                              (r) =>
                                r.institution?.id === values.institution_id &&
                                r.session?.id === values.session_id
                            )
                            .map((r) => r.focus_area?.id);

                          const excludedIds = new Set([
                            ...usedInForm,
                            ...existingForCombo,
                          ]);

                          const availableFocusAreas = focusAreas.filter(
                            (fa) =>
                              !excludedIds.has(fa.id) || fa.id === currentFAId // allow current row
                          );

                          return (
                            <div
                              key={index}
                              className="grid md:grid-cols-11 gap-4 items-end border border-gray-200 p-4 rounded-md bg-gray-50"
                            >
                              {/* Focus Area */}
                              <div className="col-span-2">
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                  Focus Area{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Select
                                  value={
                                    values.focus_area_stats[index]
                                      .focus_area_id || undefined
                                  }
                                  onChange={(val) =>
                                    setFieldValue(
                                      `focus_area_stats[${index}].focus_area_id`,
                                      val
                                    )
                                  }
                                  placeholder="Select Focus Area"
                                  className="w-full"
                                  disabled={
                                    !values.institution_id || !values.session_id
                                  }
                                >
                                  {availableFocusAreas.map((fa) => (
                                    <Option key={fa.id} value={fa.id}>
                                      {fa.name}
                                    </Option>
                                  ))}
                                </Select>
                                {errors.focus_area_stats &&
                                  errors.focus_area_stats[index]
                                    ?.focus_area_id &&
                                  touched.focus_area_stats?.[index]
                                    ?.focus_area_id && (
                                    <div className="text-red-500 text-xs">
                                      {
                                        errors.focus_area_stats[index]
                                          .focus_area_id
                                      }
                                    </div>
                                  )}
                              </div>

                              {/* PhD */}
                              <NumberInput
                                name={`focus_area_stats[${index}].phd_staff_count_full_time`}
                                label="PhD FT"
                                placeholder="e.g. 10"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />
                              <NumberInput
                                name={`focus_area_stats[${index}].phd_staff_count_part_time`}
                                label="PhD PT"
                                placeholder="e.g. 5"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />
                              <NumberInput
                                name={`focus_area_stats[${index}].phd_staff_count_retained`}
                                label="PhD Retained"
                                placeholder="e.g. 7"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Professor */}
                              <NumberInput
                                name={`focus_area_stats[${index}].professor_staff_count_full_time`}
                                label="Professor FT"
                                placeholder="e.g. 8"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />
                              <NumberInput
                                name={`focus_area_stats[${index}].professor_staff_count_part_time`}
                                label="Professor PT"
                                placeholder="e.g. 4"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />
                              <NumberInput
                                name={`focus_area_stats[${index}].professor_staff_count_retained`}
                                label="Professor Retained"
                                placeholder="e.g. 6"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Masters */}
                              <NumberInput
                                name={`focus_area_stats[${index}].masters_staff_count_full_time`}
                                label="Masters FT"
                                placeholder="e.g. 12"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />
                              <NumberInput
                                name={`focus_area_stats[${index}].masters_staff_count_part_time`}
                                label="Masters PT"
                                placeholder="e.g. 3"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />
                              <NumberInput
                                name={`focus_area_stats[${index}].masters_staff_count_retained`}
                                label="Masters Retained"
                                placeholder="e.g. 5"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Remove new rows only */}
                              {!values.focus_area_stats[index].id ? (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 justify-self-end"
                                  title="Remove row"
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              ) : (
                                <Tooltip title="Cannot delete existing row here">
                                  <button
                                    type="button"
                                    disabled
                                    className="text-gray-400 justify-self-end cursor-not-allowed"
                                  >
                                    <FiTrash2 size={18} />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          );
                        })}

                        {/* Add Focus Area */}
                        {focusAreas.length > values.focus_area_stats.length && (
                          <button
                            type="button"
                            className="mt-2 text-blue-600"
                            onClick={() =>
                              push({
                                focus_area_id: "",
                                phd_staff_count_full_time: "",
                                phd_staff_count_part_time: "",
                                phd_staff_count_retained: "",
                                professor_staff_count_full_time: "",
                                professor_staff_count_part_time: "",
                                professor_staff_count_retained: "",
                                masters_staff_count_full_time: "",
                                masters_staff_count_part_time: "",
                                masters_staff_count_retained: "",
                              })
                            }
                            disabled={
                              !values.institution_id || !values.session_id
                            }
                          >
                            + Add Focus Area
                          </button>
                        )}
                      </div>
                    )}
                  </FieldArray>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          {/* Delete modal */}
          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDelete}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={
              rowToDelete
                ? `Please confirm that you want to delete ${rowToDelete.focus_area?.name} stats for ${rowToDelete.institution?.name}`
                : ""
            }
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default StaffStatistics;
